import { useEffect, useMemo } from 'react';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { useAuth } from './auth';

/**
 * Normalize the current hostname to a stable `domain` token suitable for
 * Unleash Strategy Constraints. We can't use `useDomain()` here because
 * `DomainProvider` mounts inside `<App>`, BELOW `<FlagProvider>` in the tree
 * — so this component runs above the DomainContext. We fall back to the same
 * hostname inspection `DomainProvider` itself uses on first render, which
 * keeps the two derivations in sync.
 *
 * Returns one of:
 *   'jdsimplified'      — apex jdsimplified.com (prod)
 *   'dev-jdsimplified'  — dev.jdsimplified.com
 *   'admin'             — admin.jdsimplified.com
 *   'studicata'         — studicata.jdsimplified.com
 *   'localhost'         — localhost / 127.0.0.1 dev
 *   'other'             — anything else (Capacitor native, preview URLs, etc.)
 */
function getDomainToken(): string {
  if (typeof window === 'undefined') return 'other';
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }
  // Order matters: more-specific subdomains before the generic apex.
  if (hostname.includes('studicata.jdsimplified.com')) return 'studicata';
  if (hostname.includes('admin.jdsimplified.com')) return 'admin';
  if (hostname.includes('dev.jdsimplified.com')) return 'dev-jdsimplified';
  if (hostname.includes('jdsimplified.com')) return 'jdsimplified';

  return 'other';
}

/**
 * Derive the user's `role` for Unleash constraint targeting from the signals
 * available at this layer of the tree (above DomainProvider, above the
 * subscription / profiles.role data sources).
 *
 *   'studicata-visitor' — anonymous viewer on studicata.jdsimplified.com
 *   'admin'             — Firebase custom claim is_admin === true
 *   'user'              — any other authenticated user
 *   ''                  — anonymous on a non-studicata domain (omitted)
 *
 * Note: 'staff' / 'paid' / 'free' need profiles.role + subscription state,
 * both of which live below <FlagProvider>. A future task can hoist those or
 * push them from a deeper component if/when constraints need that granularity.
 */
function getRoleToken(opts: {
  userId: string;
  isAdmin: boolean;
  domain: string;
}): string {
  if (!opts.userId) {
    return opts.domain === 'studicata' ? 'studicata-visitor' : '';
  }
  if (opts.isAdmin) return 'admin';
  return 'user';
}

/**
 * Syncs the JDS authenticated user into the Unleash SDK context so that
 * user-ID-targeting strategies key off Firebase UID (which equals the JDS
 * user_id / profiles.id), NOT the Unleash account ID.
 *
 * Also pushes two properties for Strategy Constraint targeting:
 *   - `domain` — which JDS surface the user is currently on (jdsimplified,
 *     studicata, admin, dev-jdsimplified, localhost, other)
 *   - `role`  — coarse role token (admin / user / studicata-visitor)
 *
 * Property values are always strings; empty/undefined values are omitted
 * from the properties payload (Unleash treats missing differently from
 * empty/null in constraint evaluation).
 *
 * Must be mounted inside `<FlagProvider>` and `<AuthProvider>`. The parent
 * `main.tsx` guards on `unleashEnabled` so this component is never mounted
 * outside a FlagProvider — calling `useUnleashContext()` then is safe.
 *
 * Renders nothing.
 */
export function UnleashContextSync(): null {
  const updateContext = useUnleashContext();
  const { user } = useAuth();

  const userId = user?.id ?? '';
  const email = user?.email ?? '';
  const isAdmin = user?.user_metadata?.is_admin === true;

  // Memoize the derived context so the effect only re-fires when an input
  // actually changes — not on every render. Domain is read once per mount
  // (hostname is stable for the life of the SPA tab; a navigation to another
  // subdomain is a full page load that remounts this component).
  const ctx = useMemo(() => {
    const domain = getDomainToken();
    const role = getRoleToken({ userId, isAdmin, domain });

    // Build properties with only non-empty string values. Unleash constraints
    // treat a missing property as "no value" — passing an empty string would
    // match `IS_EMPTY` constraints unexpectedly.
    const properties: Record<string, string> = {};
    if (email) properties.email = email;
    if (domain) properties.domain = domain;
    if (role) properties.role = role;

    return { userId, properties };
  }, [userId, email, isAdmin]);

  useEffect(() => {
    // Fire-and-forget; the SDK returns a Promise that resolves after the
    // next proxy fetch, but we don't need to await it here.
    void updateContext(ctx);
  }, [updateContext, ctx]);

  return null;
}
